import { useState, useRef, useEffect, useCallback, FormEvent, ClipboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Bot, X, Send, ImagePlus, Loader2, Check, Ban, RotateCcw, AlertCircle, Clock } from 'lucide-react';
import API_CONFIG from '../config/api';
import { enTeteComptePrive } from '../utils/session';
import { endpointPushCourant } from '../utils/push';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Message tel qu'affiché dans la bulle */
interface UiMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  text: string;
  imagePreview?: string;
  error?: boolean;
  /** Écrit pendant que Jarvis travaillait : affiché, mais pas encore envoyé */
  enAttente?: boolean;
  /** Son entrée dans la file, pour le retrouver au moment du départ */
  fileId?: string;
}

/** Demande écrite pendant que Jarvis était occupé, gardée pour plus tard */
interface EnAttente {
  id: string;
  texte: string;
  image?: { dataUrl: string; mimeType: string };
}

/** Message au format OpenAI, renvoyé/attendu par le serveur */
interface ApiMessage {
  role: 'user' | 'assistant' | 'tool';
  content: unknown;
  tool_calls?: unknown;
  tool_call_id?: string;
  name?: string;
}

interface PendingAction {
  tool: string;
  args: Record<string, unknown>;
  titre: string;
  lignes: { label: string; valeur: string }[];
}

/** Réponse des routes /chat, /execute et /jobs */
interface ReponseJarvis {
  success: boolean;
  error?: string;
  reply?: string;
  history?: ApiMessage[];
  pendingAction?: PendingAction;
  tacheId?: string;
  enCours?: boolean;
}

/** Demande en cours de traitement sur le serveur */
interface TacheEnCours {
  id: string;
  type: 'chat' | 'execute';
  creeLe: number;
}

const SUGGESTIONS = [
  'Quels sont mes rendez-vous cette semaine ?',
  'Donne-moi la fiche du client Tremblay',
  'Combien de clients à Laval ?',
  'Prends un rdv pour Marc Gagnon demain à 9h',
];

const STORAGE_KEY = 'aquarius_jarvis_conversation';
const MAX_IMAGE_SIDE = 1400;
// Une seconde : chaque tour d'attente retardait la réponse affichée d'une seconde en moyenne.
const POLL_INTERVAL_MS = 1000;
const POLL_TIMEOUT_MS = 15000;
const MAX_ATTENTE_MS = 10 * 60 * 1000;

const MESSAGES_TACHE_PERDUE: Record<TacheEnCours['type'], string> = {
  chat: "Jarvis n'a pas reçu ton message (connexion coupée ou serveur redémarré). Renvoie-le.",
  execute:
    "Jarvis a perdu la trace de cette action (connexion coupée ou serveur redémarré). Vérifie dans Square avant de réessayer.",
};

const MESSAGES_TACHE_TROP_LONGUE: Record<TacheEnCours['type'], string> = {
  chat: 'Jarvis met trop de temps à répondre. Réessaie.',
  execute: "L'action ne s'est pas terminée à temps. Vérifie dans Square avant de réessayer.",
};

// ---------------------------------------------------------------------------
// Utilitaires
// ---------------------------------------------------------------------------

const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Redimensionne et compresse une image avant l'envoi : une capture d'écran de
 * téléphone brute pèse plusieurs Mo, ce qui est inutile pour la lecture du texte.
 */
function compressImage(file: File): Promise<{ dataUrl: string; mimeType: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Impossible de lire l'image"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Image illisible"));
      img.onload = () => {
        const ratio = Math.min(1, MAX_IMAGE_SIDE / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * ratio);
        canvas.height = Math.round(img.height * ratio);
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('Canvas indisponible'));
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve({ dataUrl: canvas.toDataURL('image/jpeg', 0.85), mimeType: 'image/jpeg' });
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Rendu markdown minimal : le modèle répond avec du **gras** et des listes « - ».
 * On évite une dépendance supplémentaire pour ces deux seuls cas.
 */
function renderMarkdown(texte: string) {
  return texte.split('\n').map((ligne, i) => {
    const puce = /^\s*[-*•]\s+/.test(ligne);
    const contenu = puce ? ligne.replace(/^\s*[-*•]\s+/, '') : ligne;

    const morceaux = contenu.split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
      part.startsWith('**') && part.endsWith('**') && part.length > 4 ? (
        <strong key={j} className="font-semibold text-cyan-200">
          {part.slice(2, -2)}
        </strong>
      ) : (
        <span key={j}>{part}</span>
      )
    );

    if (puce) {
      return (
        <span key={i} className="flex gap-1.5">
          <span className="text-cyan-400 shrink-0">•</span>
          <span>{morceaux}</span>
        </span>
      );
    }
    return <span key={i} className="block">{morceaux}</span>;
  });
}

/**
 * Allège l'historique conservé côté client : on remplace les images déjà
 * analysées par un simple marqueur texte pour ne pas les renvoyer à chaque tour.
 */
function stripImages(history: ApiMessage[]): ApiMessage[] {
  return history.map((m) => {
    if (!Array.isArray(m.content)) return m;
    const texte = (m.content as { type: string; text?: string }[])
      .filter((p) => p.type === 'text' && p.text)
      .map((p) => p.text)
      .join(' ');
    return { ...m, content: `${texte} [capture d'écran analysée]`.trim() };
  });
}

/** Conversation laissée par une session précédente */
function lireSauvegarde(): {
  messages?: UiMessage[];
  history?: ApiMessage[];
  pending?: PendingAction | null;
  tache?: TacheEnCours | null;
  fileAttente?: EnAttente[];
} {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {};
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return {};
  }
}

const nouvelIdTache = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${uid()}-${Math.random().toString(36).slice(2, 10)}`;

const pause = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Vient chercher le résultat d'une demande traitée par le serveur. Un échec
 * réseau n'est pas fatal : quand on quitte l'app, iOS coupe les requêtes et
 * suspend les minuteries, la boucle reprend simplement au retour.
 */
async function attendreTache(tache: TacheEnCours): Promise<ReponseJarvis> {
  for (;;) {
    await pause(POLL_INTERVAL_MS);

    let introuvable = false;
    const ctrl = new AbortController();
    const minuterie = setTimeout(() => ctrl.abort(), POLL_TIMEOUT_MS);
    try {
      const resp = await fetch(`${API_CONFIG.endpoints.assistantJobs}/${tache.id}`, { signal: ctrl.signal });
      if (resp.status === 404) {
        introuvable = true;
      } else {
        const data = await resp.json();
        if (!data.enCours) return data;
      }
    } catch {
      // réseau coupé ou réponse illisible (serveur qui redémarre) : on réessaie
    } finally {
      clearTimeout(minuterie);
    }

    if (introuvable) throw new Error(MESSAGES_TACHE_PERDUE[tache.type]);
    if (Date.now() - tache.creeLe > MAX_ATTENTE_MS) throw new Error(MESSAGES_TACHE_TROP_LONGUE[tache.type]);
  }
}

/**
 * Envoie une demande à traiter en arrière-plan puis attend son résultat. Si
 * l'envoi lui-même est coupé (app quittée juste après), la demande est peut-être
 * quand même arrivée : son identifiant permet d'aller vérifier.
 */
async function lancerTache(
  url: string,
  body: Record<string, unknown>,
  tache: TacheEnCours
): Promise<ReponseJarvis> {
  try {
    // Si les notifications sont activées ici, le serveur prévient cet appareil
    // quand la réponse est prête et que l'app est passée en arrière-plan.
    const abonnementPush = await endpointPushCourant();
    const resp = await fetch(url, {
      method: 'POST',
      // Le jeton du compte privé débloque l'outil « statistiques » de Jarvis
      headers: { 'Content-Type': 'application/json', ...enTeteComptePrive() },
      body: JSON.stringify({ ...body, tacheId: tache.id, abonnementPush }),
    });
    const data = await resp.json();
    // Serveur pas encore mis à jour, ou demande refusée d'emblée : réponse directe
    if (!data.tacheId) return data;
  } catch {
    // connexion coupée pendant l'envoi : on vérifie ci-dessous
  }
  return attendreTache(tache);
}

// ---------------------------------------------------------------------------
// Composant
// ---------------------------------------------------------------------------

const JarvisBubble = () => {
  // Conversation d'une session précédente, lue une seule fois au montage
  const [sauvegarde] = useState(lireSauvegarde);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<UiMessage[]>(() =>
    Array.isArray(sauvegarde.messages) ? sauvegarde.messages : []
  );
  const [history, setHistory] = useState<ApiMessage[]>(() =>
    Array.isArray(sauvegarde.history) ? sauvegarde.history : []
  );
  const [pending, setPending] = useState<PendingAction | null>(sauvegarde.pending ?? null);
  // Plusieurs clients veulent un rendez-vous en même temps : ce qu'on écrit
  // pendant que Jarvis travaille est mis de côté et part dès qu'il a fini,
  // au lieu d'être refusé par un champ grisé.
  const [fileAttente, setFileAttente] = useState<EnAttente[]>(
    Array.isArray(sauvegarde.fileAttente) ? sauvegarde.fileAttente : []
  );
  const [input, setInput] = useState('');
  const [image, setImage] = useState<{ dataUrl: string; mimeType: string } | null>(null);
  // Capture ouverte en grand : en vignette, le texte d'une fiche de contact
  // (téléphone, adresse) est illisible — impossible de relire ce qu'on a envoyé.
  const [zoom, setZoom] = useState<string | null>(null);
  /** Vraie taille, défilable : pour lire les petits caractères sur le téléphone */
  const [zoomTailleReelle, setZoomTailleReelle] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // Sauvegardée avec la conversation : si iOS décharge la page pendant qu'on
  // est dans une autre app, l'attente reprend au retour.
  const [tache, setTache] = useState<TacheEnCours | null>(null);
  const tacheRef = useRef<string | null>(null);
  const repriseFaite = useRef(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          messages: messages.slice(-40),
          history: history.slice(-40),
          pending,
          tache,
          fileAttente,
        })
      );
    } catch {
      // quota dépassé : on ignore, la conversation reste en mémoire
    }
  }, [messages, history, pending, tache, fileAttente]);

  // Auto-scroll vers le dernier message
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, pending, open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 150);
  }, [open]);

  // Ouverture depuis une notification : app déjà ouverte (événement relayé par
  // le service worker) ou lancée par le clic (?jarvis dans l'adresse)
  useEffect(() => {
    const ouvrir = () => setOpen(true);
    window.addEventListener('jarvis:ouvrir', ouvrir);

    const params = new URLSearchParams(window.location.search);
    if (params.has('jarvis')) {
      setOpen(true);
      params.delete('jarvis');
      const reste = params.toString();
      window.history.replaceState(window.history.state, '', window.location.pathname + (reste ? `?${reste}` : ''));
    }

    return () => window.removeEventListener('jarvis:ouvrir', ouvrir);
  }, []);

  const ouvrirZoom = useCallback((source: string) => {
    setZoomTailleReelle(false);
    setZoom(source);
  }, []);

  const fermerZoom = useCallback(() => {
    setZoom(null);
    setZoomTailleReelle(false);
  }, []);

  // Sans ça, faire glisser la capture agrandie sur le téléphone fait défiler
  // la page du tableau de bord derrière elle.
  useEffect(() => {
    if (!zoom) return;
    const avant = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = avant;
    };
  }, [zoom]);

  // Fermer avec Échap : la capture agrandie d'abord, le panneau ensuite
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (zoom) fermerZoom();
      else setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, zoom, fermerZoom]);

  const pushMessage = useCallback((msg: Omit<UiMessage, 'id'>) => {
    setMessages((prev) => [...prev, { ...msg, id: uid() }]);
  }, []);

  const handleFile = useCallback(async (file: File | null | undefined) => {
    if (!file || !file.type.startsWith('image/')) return;
    try {
      setImage(await compressImage(file));
    } catch (err) {
      pushMessage({ role: 'system', text: err instanceof Error ? err.message : "Image illisible", error: true });
    }
  }, [pushMessage]);

  // Coller une capture d'écran directement dans le champ
  const handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const item = Array.from(e.clipboardData.items).find((i) => i.type.startsWith('image/'));
    if (item) {
      e.preventDefault();
      handleFile(item.getAsFile());
    }
  };

  /**
   * Lance (avec `envoi`) ou reprend une demande traitée en arrière-plan par le
   * serveur : quitter l'app pendant que Jarvis travaille ne perd plus la réponse.
   */
  const suivreTache = useCallback(
    async (t: TacheEnCours, envoi?: { url: string; body: Record<string, unknown> }) => {
      tacheRef.current = t.id;
      setTache(t);
      if (t.type === 'chat') setLoading(true);
      else setConfirming(true);

      try {
        const data = envoi ? await lancerTache(envoi.url, envoi.body, t) : await attendreTache(t);
        if (tacheRef.current !== t.id) return; // conversation réinitialisée entre-temps
        if (!data.success) {
          throw new Error(data.error || (t.type === 'chat' ? 'Erreur du serveur' : "L'action a échoué"));
        }

        setHistory(stripImages(data.history || []));
        pushMessage({ role: 'assistant', text: data.reply ?? '' });
        if (t.type === 'execute') setPending(null);
        else if (data.pendingAction) setPending(data.pendingAction);
      } catch (err) {
        if (tacheRef.current !== t.id) return;
        pushMessage({
          role: 'system',
          text:
            err instanceof Error
              ? err.message
              : t.type === 'chat'
                ? 'Impossible de joindre Jarvis'
                : "L'action a échoué",
          error: true,
        });
      } finally {
        if (tacheRef.current === t.id) {
          tacheRef.current = null;
          setTache(null);
          setLoading(false);
          setConfirming(false);
        }
      }
    },
    [pushMessage]
  );

  // Page rechargée pendant qu'une demande était en cours : on reprend l'attente
  useEffect(() => {
    if (repriseFaite.current) return;
    repriseFaite.current = true;
    if (sauvegarde.tache && typeof sauvegarde.tache.id === 'string') suivreTache(sauvegarde.tache);
  }, [sauvegarde, suivreTache]);

  const envoyerChat = useCallback(
    (texte: string, img?: { dataUrl: string; mimeType: string } | null) => {
      suivreTache(
        { id: nouvelIdTache(), type: 'chat', creeLe: Date.now() },
        {
          url: API_CONFIG.endpoints.assistantChat,
          body: {
            message: texte,
            image: img ? { data: img.dataUrl, mimeType: img.mimeType } : undefined,
            history,
          },
        }
      );
    },
    [history, suivreTache]
  );

  const send = (e?: FormEvent) => {
    e?.preventDefault();
    const texte = input.trim();
    if (!texte && !image) return;

    const imageEnvoyee = image;
    setInput('');
    setImage(null);

    // Jarvis réfléchit, ou attend une confirmation : on garde la demande pour
    // plus tard. L'envoyer maintenant couperait ce qu'il est en train de faire.
    if (loading || tache || pending) {
      const id = uid();
      pushMessage({
        role: 'user',
        text: texte,
        imagePreview: imageEnvoyee?.dataUrl,
        enAttente: true,
        fileId: id,
      });
      setFileAttente((file) => [...file, { id, texte, image: imageEnvoyee ?? undefined }]);
      return;
    }

    pushMessage({ role: 'user', text: texte, imagePreview: imageEnvoyee?.dataUrl });
    setPending(null);
    envoyerChat(texte, imageEnvoyee);
  };

  // Dès que Jarvis se libère, la demande suivante part toute seule. On attend
  // aussi qu'aucune confirmation ne soit en suspens : envoyer maintenant
  // ferait disparaître la carte avant que l'utilisateur ait tranché.
  useEffect(() => {
    // tacheRef est posée dans la foulée de l'envoi, là où `tache` n'est encore
    // que programmée : sans elle, deux passages d'affilée enverraient deux fois.
    if (tacheRef.current || loading || tache || pending || fileAttente.length === 0) return;

    const [suivant, ...reste] = fileAttente;
    setFileAttente(reste);
    setMessages((prev) =>
      prev.map((m) => (m.fileId === suivant.id ? { ...m, enAttente: false, fileId: undefined } : m))
    );
    envoyerChat(suivant.texte, suivant.image);
  }, [loading, tache, pending, fileAttente, envoyerChat]);

  const confirmAction = () => {
    if (!pending || confirming || tache) return;
    suivreTache(
      { id: nouvelIdTache(), type: 'execute', creeLe: Date.now() },
      { url: API_CONFIG.endpoints.assistantExecute, body: { tool: pending.tool, args: pending.args, history } }
    );
  };

  const cancelAction = () => {
    setPending(null);
    pushMessage({ role: 'system', text: 'Action annulée.' });
  };

  const reset = () => {
    // Une demande encore en cours côté serveur se termine, mais sa réponse
    // n'apparaîtra pas dans la nouvelle conversation.
    tacheRef.current = null;
    setTache(null);
    setLoading(false);
    setConfirming(false);
    setMessages([]);
    setHistory([]);
    setPending(null);
    setFileAttente([]);
    setImage(null);
    setInput('');
    localStorage.removeItem(STORAGE_KEY);
  };

  return createPortal(
    <>
      {/* Bulle flottante permanente */}
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? 'Fermer Jarvis' : 'Ouvrir Jarvis'}
        className="fixed z-[9999] right-4 bottom-[max(1rem,env(safe-area-inset-bottom,0px))] h-14 w-14 rounded-full flex items-center justify-center bg-gradient-to-br from-indigo-500 to-cyan-500 border border-cyan-300/50 shadow-lg shadow-cyan-500/40 hover:shadow-cyan-400/60 hover:scale-105 active:scale-95 transition-all duration-200"
      >
        <span className="absolute inset-0 rounded-full bg-cyan-400/30 animate-ping-slow pointer-events-none" />
        {open ? (
          <X className="h-6 w-6 text-white relative" />
        ) : (
          <Bot className="h-7 w-7 text-white relative drop-shadow-[0_0_6px_rgba(255,255,255,0.8)]" />
        )}
      </button>

      {/* Panneau de conversation */}
      {open && (
        <div className="fixed z-[9998] right-2 left-2 sm:left-auto bottom-[calc(4.5rem+env(safe-area-inset-bottom,0px))] sm:right-4 sm:w-[26rem] max-h-[min(34rem,calc(100vh-8rem))] flex flex-col rounded-2xl overflow-hidden border border-indigo-500/40 bg-gradient-to-br from-gray-950 to-indigo-950 shadow-2xl shadow-indigo-500/30 animate-jarvis-in">
          {/* En-tête */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-indigo-500/30 bg-gradient-to-r from-indigo-950/80 to-purple-950/80 shrink-0">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-indigo-500/40 to-cyan-500/40 border border-cyan-400/40 flex items-center justify-center">
                <Bot className="h-4 w-4 text-cyan-300" />
              </div>
              <div>
                <p className="text-sm font-semibold bg-gradient-to-r from-cyan-300 to-indigo-300 bg-clip-text text-transparent">
                  Jarvis
                </p>
                <p className="text-[10px] text-gray-400 leading-none">Assistant Aquarius</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={reset}
                title="Nouvelle conversation"
                className="p-1.5 rounded-md text-gray-400 hover:text-cyan-300 hover:bg-cyan-500/10 transition-colors"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
              <button
                onClick={() => setOpen(false)}
                title="Fermer"
                className="p-1.5 rounded-md text-gray-400 hover:text-rose-300 hover:bg-rose-500/10 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Fil de discussion */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3 min-h-[12rem]">
            {messages.length === 0 && (
              <div className="text-center py-4">
                <p className="text-sm text-gray-300 mb-1">Salut 👋</p>
                <p className="text-xs text-gray-500 mb-4 px-4">
                  Pose-moi une question sur les clients ou les rendez-vous, demande-moi d'en créer un,
                  ou envoie-moi une capture d'écran.
                </p>
                <div className="space-y-1.5">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => setInput(s)}
                      className="block w-full text-left text-xs px-3 py-2 rounded-lg border border-indigo-500/25 bg-indigo-500/5 text-gray-300 hover:border-cyan-400/40 hover:bg-cyan-500/10 hover:text-cyan-200 transition-all"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m) => {
              if (m.role === 'system') {
                return (
                  <div
                    key={m.id}
                    className={`flex items-start gap-2 text-xs px-3 py-2 rounded-lg border ${
                      m.error
                        ? 'border-rose-500/40 bg-rose-500/10 text-rose-200'
                        : 'border-gray-600/40 bg-gray-700/20 text-gray-400'
                    }`}
                  >
                    {m.error && <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />}
                    <span>{m.text}</span>
                  </div>
                );
              }

              const isUser = m.role === 'user';
              return (
                <div key={m.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm break-words leading-relaxed ${
                      isUser
                        ? 'bg-gradient-to-br from-indigo-500/30 to-purple-500/30 border border-indigo-400/40 text-gray-100 rounded-br-sm'
                        : 'bg-gray-800/60 border border-cyan-500/25 text-gray-200 rounded-bl-sm'
                    } ${m.enAttente ? 'border-dashed opacity-70' : ''}`}
                  >
                    {m.imagePreview && (
                      <button
                        type="button"
                        onClick={() => ouvrirZoom(m.imagePreview as string)}
                        aria-label="Agrandir la capture"
                        className="block mb-2 rounded-lg overflow-hidden border border-indigo-400/30 hover:border-cyan-300/70 focus:outline-none focus:border-cyan-300/70 transition-colors cursor-zoom-in"
                      >
                        <img src={m.imagePreview} alt="Capture envoyée" className="max-h-40 w-auto" />
                      </button>
                    )}
                    {isUser ? <span className="whitespace-pre-wrap">{m.text}</span> : renderMarkdown(m.text)}
                    {m.enAttente && (
                      <span className="mt-1.5 flex items-center gap-1 text-[10px] text-indigo-200/70">
                        <Clock className="h-3 w-3 shrink-0" />
                        En attente — part dès que Jarvis a fini
                      </span>
                    )}
                  </div>
                </div>
              );
            })}

            {loading && (
              <div className="flex justify-start">
                <div className="rounded-2xl rounded-bl-sm px-3 py-2 bg-gray-800/60 border border-cyan-500/25 flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 text-cyan-300 animate-spin" />
                  <span className="text-xs text-gray-400">
                    Jarvis réfléchit… tu peux quitter l'app
                    {fileAttente.length > 0 &&
                      ` · ${fileAttente.length} demande${fileAttente.length > 1 ? 's' : ''} en attente`}
                  </span>
                </div>
              </div>
            )}

            {/* Carte de confirmation d'une action d'écriture */}
            {pending && (
              <div className="rounded-xl border border-amber-400/40 bg-amber-500/10 p-3">
                <p className="text-xs font-semibold text-amber-200 mb-2">{pending.titre}</p>
                <dl className="space-y-1 mb-3">
                  {pending.lignes.map((l) => (
                    <div key={l.label} className="flex gap-2 text-xs">
                      <dt className="text-amber-200/60 shrink-0 w-24">{l.label}</dt>
                      <dd className="text-gray-100 break-words">{l.valeur}</dd>
                    </div>
                  ))}
                </dl>
                <div className="flex gap-2">
                  <button
                    onClick={confirmAction}
                    disabled={confirming}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gradient-to-r from-emerald-500/30 to-teal-500/30 border border-emerald-400/50 text-emerald-100 hover:from-emerald-500/45 hover:to-teal-500/45 disabled:opacity-50 transition-all"
                  >
                    {confirming ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                    Confirmer
                  </button>
                  <button
                    onClick={cancelAction}
                    disabled={confirming}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-700/40 border border-gray-500/40 text-gray-300 hover:bg-gray-700/60 disabled:opacity-50 transition-all"
                  >
                    <Ban className="h-3.5 w-3.5" />
                    Annuler
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Zone de saisie */}
          <form onSubmit={send} className="border-t border-indigo-500/30 p-2 shrink-0 bg-gray-950/60">
            {image && (
              <div className="relative inline-block mb-2">
                <button
                  type="button"
                  onClick={() => ouvrirZoom(image.dataUrl)}
                  aria-label="Agrandir la capture"
                  className="block rounded-lg overflow-hidden border border-cyan-400/40 hover:border-cyan-300/80 transition-colors cursor-zoom-in"
                >
                  <img src={image.dataUrl} alt="À envoyer" className="h-16 w-auto" />
                </button>
                <button
                  type="button"
                  onClick={() => setImage(null)}
                  className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-rose-500 text-white flex items-center justify-center shadow-lg"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            <div className="flex items-end gap-1.5">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                title="Joindre une capture d'écran"
                className="p-2 rounded-lg text-gray-400 hover:text-cyan-300 hover:bg-cyan-500/10 transition-colors shrink-0"
              >
                <ImagePlus className="h-5 w-5" />
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  handleFile(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onPaste={handlePaste}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                rows={1}
                placeholder={
                  loading || tache || pending ? 'Jarvis travaille — ça partira après…' : 'Écris à Jarvis…'
                }
                className="flex-1 resize-none max-h-24 bg-gray-900/70 border border-indigo-500/30 rounded-xl px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:border-cyan-400/60 focus:ring-1 focus:ring-cyan-400/30 transition-all"
              />
              <button
                type="submit"
                disabled={!input.trim() && !image}
                className="p-2 rounded-lg bg-gradient-to-br from-indigo-500/40 to-cyan-500/40 border border-cyan-400/40 text-cyan-100 hover:from-indigo-500/60 hover:to-cyan-500/60 disabled:opacity-40 disabled:hover:from-indigo-500/40 transition-all shrink-0"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Capture en grand. Un clic sur l'image bascule en taille réelle, défilable :
          c'est ainsi qu'on relit un numéro de téléphone sur une fiche de contact. */}
      {zoom && (
        <div
          className="fixed inset-0 z-[10000] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 animate-jarvis-in"
          onClick={fermerZoom}
          role="dialog"
          aria-modal="true"
          aria-label="Capture agrandie"
        >
          <div
            className={`max-h-full max-w-full ${zoomTailleReelle ? 'overflow-auto' : 'flex items-center justify-center'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={zoom}
              alt="Capture agrandie"
              onClick={() => setZoomTailleReelle((r) => !r)}
              className={
                zoomTailleReelle
                  ? 'max-w-none cursor-zoom-out'
                  : 'max-h-[85vh] max-w-full object-contain rounded-lg cursor-zoom-in'
              }
            />
          </div>

          <button
            type="button"
            onClick={fermerZoom}
            aria-label="Fermer la capture"
            className="absolute right-3 top-[max(0.75rem,env(safe-area-inset-top,0px))] h-9 w-9 rounded-full bg-gray-900/90 border border-gray-500/60 text-gray-100 flex items-center justify-center hover:bg-gray-800 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>

          <p className="absolute left-0 right-0 bottom-[max(0.75rem,env(safe-area-inset-bottom,0px))] text-center pointer-events-none">
            <span className="inline-block rounded-full bg-gray-900/85 px-3 py-1 text-[11px] text-gray-300">
              {zoomTailleReelle ? "Touche l'image pour la réduire" : "Touche l'image pour zoomer"} · touche le fond pour
              fermer
            </span>
          </p>
        </div>
      )}

      <style>{`
        @keyframes jarvis-in {
          from { opacity: 0; transform: translateY(12px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-jarvis-in { animation: jarvis-in 0.18s ease-out; }

        @keyframes ping-slow {
          0%   { transform: scale(1);   opacity: 0.5; }
          70%  { transform: scale(1.5); opacity: 0; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        .animate-ping-slow { animation: ping-slow 2.5s cubic-bezier(0, 0, 0.2, 1) infinite; }
      `}</style>
    </>,
    document.body
  );
};

export default JarvisBubble;
